import { useRecoilValue } from 'recoil'

import CardWithTabs from '../../../components/CardWithTabs'
import LineChart from '../../../components/LineChart'
import useLocationDateTimeForm from '../../../hooks/useLocationDateTimeForm'
import { ServiceAtom } from '../../../recoil/atoms'
import useGetStoreVisitCount from '../hooks/useGetStoreVisitCount'
import useGetStoreVisitCountUUDaily from '../hooks/useGetStoreVisitCountUUDaily'
import useGetStoreVisitCountUUDateRange from '../hooks/useGetStoreVisitCountUUDateRange'
import useGetStoreVisitDwellTime from '../hooks/useGetStoreVisitDwellTime'

interface Props {
  className?: string
}

function UserVisitPerformance({ className }: Props) {
  const { form } = useLocationDateTimeForm()
  const service = useRecoilValue(ServiceAtom)

  const { uniqueUsers, uniqueUsersLoading } = useGetStoreVisitCountUUDateRange({
    serviceId: service.value,
    siteId: form.siteId,
    floorId: form.floorId,
    siteGroupId: form.siteGroupId,
    fromDate: form.fromDate,
    toDate: form.toDate,
  })
  const { storeVisitCountUUDaily, storeVisitCountUUDailyLoading } = useGetStoreVisitCountUUDaily({
    serviceId: service.value,
    siteId: form.siteId,
    floorId: form.floorId,
    siteGroupId: form.siteGroupId,
    fromDate: form.fromDate,
    toDate: form.toDate,
  })
  const { storeVisitCount, storeVisitCountLoading } = useGetStoreVisitCount({
    serviceId: service.value,
    siteId: form.siteId,
    floorId: form.floorId,
    siteGroupId: form.siteGroupId,
    fromDate: form.fromDate,
    toDate: form.toDate,
  })
  const { storeVisitDwellTime, storeVisitDwellTimeLoading } = useGetStoreVisitDwellTime({
    serviceId: service.value,
    siteId: form.siteId,
    floorId: form.floorId,
    siteGroupId: form.siteGroupId,
    fromDate: form.fromDate,
    toDate: form.toDate,
  })

  return (
    <CardWithTabs
      className={className}
      tabs={[
        {
          title: '期間ユニーク',
          value: 'unique',
          latest: uniqueUsers?.currentPeriodSum ?? 0,
          oldest: uniqueUsers?.previousPeriodSum ?? 0,
          qty: '人',
          isLoading: uniqueUsersLoading,
        },
        {
          title: 'デイリーユニーク',
          value: 'daily-unique',
          latest: storeVisitCountUUDaily?.currentPeriodValue ?? 0,
          oldest: storeVisitCountUUDaily?.previousPeriodValue ?? 0,
          qty: '人',
          children: (
            <LineChart
              groupLabel='デイリーユニーク'
              labels={storeVisitCountUUDaily?.storeVisitCountUUDailyDatas?.map((data) => data.date) ?? []}
              datas={storeVisitCountUUDaily?.storeVisitCountUUDailyDatas?.map((data) => data.value) ?? []}
              xAxisLabel='日付'
              yAxisLabel='人'
            />
          ),
          isLoading: storeVisitCountUUDailyLoading,
        },
        {
          title: '来訪回数',
          value: 'visit',
          latest: storeVisitCount?.currentPeriodValue ?? 0,
          oldest: storeVisitCount?.previousPeriodValue ?? 0,
          qty: '回',
          children: (
            <LineChart
              groupLabel='来訪回数'
              labels={storeVisitCount?.storeVisitCountDatas?.map((data) => data.date) ?? []}
              datas={storeVisitCount?.storeVisitCountDatas?.map((data) => data.value) ?? []}
              xAxisLabel='日付'
              yAxisLabel='回'
            />
          ),
          isLoading: storeVisitCountLoading,
        },
        {
          title: 'アプリ平均利用時間',
          value: 'utilized-time',
          latest: storeVisitDwellTime ? Math.round(storeVisitDwellTime.currentPeriodValue / 60) : 0,
          oldest: storeVisitDwellTime ? Math.round(storeVisitDwellTime.previousPeriodValue / 60) : 0,
          qty: '分',
          children: (
            <LineChart
              groupLabel='アプリ平均利用時間'
              labels={storeVisitDwellTime?.storeVisitAvgDwellTimeDatas?.map((data) => data.date) ?? []}
              datas={storeVisitDwellTime?.storeVisitAvgDwellTimeDatas?.map((data) => Math.round(data.value / 60)) ?? []}
              xAxisLabel='日付'
              yAxisLabel='分'
            />
          ),
          isLoading: storeVisitDwellTimeLoading,
        },
      ]}
    />
  )
}

UserVisitPerformance.defaultProps = {
  className: '',
}

export default UserVisitPerformance
